'use client';

import * as React from 'react';
import { Popover, usePopover, type PopoverPlacement } from './Popover';

export interface TooltipProps {
  /* The trigger element. Cloned with hover / focus event listeners
     to show/hide the tooltip; ref is composed with usePopover's
     anchor ref. */
  children: React.ReactElement<Record<string, unknown>>;
  /* The tooltip's content — typically short, descriptive text
     (no interactive elements; tooltips don't trap focus). */
  content: React.ReactNode;
  /* Delay in ms before the tooltip shows after hover/focus begins.
     Default 200ms matches the WAI-ARIA APG tooltip pattern guidance
     (long enough to skip during fast mouse motion, short enough to
     feel responsive on intentional hover). */
  delay?: number;
  placement?: PopoverPlacement;
}

function composeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === 'function') ref(node);
      else (ref as React.MutableRefObject<T>).current = node;
    }
  };
}

/**
 * Tooltip — a small label that appears on hover or keyboard focus
 * and describes the trigger element. Composes <Popover> with
 * `panelRole="tooltip"` so usePopover wires aria-describedby on
 * the trigger (per WAI-ARIA APG: aria-haspopup="tooltip" is NOT
 * a valid value; tooltips are described, not held-as-popups).
 *
 * Open/close model:
 * - Mouse: hover-in → after `delay` ms → show; hover-out → hide
 * - Keyboard: focus → show immediately (no delay; keyboard
 *   intentionality is already there); blur → hide
 * - Escape: hide
 *
 * Tooltips MUST NOT contain interactive elements per APG. The
 * trigger remains the only focusable element; the tooltip is
 * described-by the trigger, not focused itself.
 */
export function Tooltip({ children, content, delay = 200, placement = 'top' }: TooltipProps) {
  const pop = usePopover<HTMLElement>({ panelRole: 'tooltip' });
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Clear any pending show-timer on unmount so it can't fire
     setIsOpen on an unmounted hook instance. */
  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const scheduleShow = React.useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      pop.open();
    }, delay);
  }, [pop, delay]);

  const cancelShowAndHide = React.useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    pop.close();
  }, [pop]);

  /* Listen for Escape to dismiss the tooltip per APG. */
  React.useEffect(() => {
    if (!pop.isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        pop.close();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pop]);

  const triggerProps = children.props as {
    onMouseEnter?: React.MouseEventHandler<HTMLElement>;
    onMouseLeave?: React.MouseEventHandler<HTMLElement>;
    onFocus?: React.FocusEventHandler<HTMLElement>;
    onBlur?: React.FocusEventHandler<HTMLElement>;
    onClick?: React.MouseEventHandler<HTMLElement>;
  };
  const triggerRef = (children as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref;

  /* Spread anchorProps FIRST so aria-describedby lands; then
     override ref + handlers. We DON'T want pop.anchorProps.onClick
     — tooltips don't toggle on click. We DO want our composed ref
     (anchor + trigger original ref). */
  const triggerEl = React.cloneElement(children, {
    ...pop.anchorProps,
    ref: composeRefs(triggerRef, pop.anchorRef),
    onClick: triggerProps.onClick,
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      triggerProps.onMouseEnter?.(e);
      scheduleShow();
    },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
      triggerProps.onMouseLeave?.(e);
      cancelShowAndHide();
    },
    onFocus: (e: React.FocusEvent<HTMLElement>) => {
      triggerProps.onFocus?.(e);
      /* Keyboard focus: show immediately. No delay because the user
         already deliberately moved focus here. */
      if (timerRef.current) clearTimeout(timerRef.current);
      pop.open();
    },
    onBlur: (e: React.FocusEvent<HTMLElement>) => {
      triggerProps.onBlur?.(e);
      cancelShowAndHide();
    },
  } as Record<string, unknown>);

  return (
    <>
      {triggerEl}
      <Popover
        {...pop.popoverProps}
        placement={placement}
        panelRole="tooltip"
        ariaLabel={typeof content === 'string' ? content : 'tooltip'}
      >
        {content}
      </Popover>
    </>
  );
}
